import { Trans, useTranslation } from '@jq4race/i18n';
import { Button } from '@jq4race/ui';
import { Share, X } from 'lucide-react';
import { useEffect, useState } from 'react';

const DISMISS_KEY = 'jq4-install-dismissed';

// Chromium-only; iOS Safari does not expose it. We type just what we use.
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const matchMedia = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  // Safari iOS exposes a non-standard `navigator.standalone` property.
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return matchMedia || iosStandalone;
}

function isIosSafari(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) && !('MSStream' in window);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIos && isSafari;
}

export function InstallPrompt() {
  const { t } = useTranslation('shell');
  const [chromePrompt, setChromePrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosBanner, setShowIosBanner] = useState(false);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  });

  useEffect(() => {
    if (dismissed || isStandalone()) return;

    if (isIosSafari()) {
      setShowIosBanner(true);
      return;
    }

    function onBeforeInstall(e: Event) {
      e.preventDefault();
      setChromePrompt(e as BeforeInstallPromptEvent);
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, [dismissed]);

  function dismiss() {
    setDismissed(true);
    setChromePrompt(null);
    setShowIosBanner(false);
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // localStorage may be blocked in private mode; non-fatal.
    }
  }

  async function triggerChromeInstall() {
    if (!chromePrompt) return;
    await chromePrompt.prompt();
    await chromePrompt.userChoice;
    // The prompt can only run once per event; mark dismissed regardless of outcome.
    dismiss();
  }

  if (dismissed) return null;
  if (chromePrompt) {
    return (
      <aside
        className="fixed inset-x-4 bottom-20 z-50 mx-auto max-w-sm rounded-xl border border-border bg-card shadow-lg"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        aria-label={t('install.promptAria')}
      >
        <div className="flex items-start gap-3 p-4">
          <div className="flex-1">
            <p className="jq-body text-foreground font-semibold">{t('install.installTitle')}</p>
            <p className="mt-1 jq-body-sm text-muted-foreground">
              {t('install.installDescription')}
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="flex h-11 w-11 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            aria-label={t('install.dismissAria')}
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
        <div className="px-4 pb-4">
          <Button size="lg" className="w-full" onClick={triggerChromeInstall}>
            {t('install.install')}
          </Button>
        </div>
      </aside>
    );
  }

  if (showIosBanner) {
    return (
      <aside
        className="fixed inset-x-4 bottom-20 z-50 mx-auto max-w-sm rounded-xl border border-border bg-card shadow-lg"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        aria-label={t('install.promptAria')}
      >
        <div className="flex items-start gap-3 p-4">
          <Share className="mt-1 size-5 shrink-0 text-jq-gold-bright" aria-hidden />
          <div className="flex-1">
            <p className="jq-body text-foreground font-semibold">
              {t('install.addToHomeScreenTitle')}
            </p>
            <p className="mt-1 jq-body-sm text-muted-foreground">
              <Trans
                i18nKey="shell:install.addToHomeScreenDescription"
                components={{
                  share: <strong className="text-foreground" />,
                  addHome: <strong className="text-foreground" />,
                }}
              />
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="flex h-11 w-11 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            aria-label={t('install.dismissAria')}
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
      </aside>
    );
  }

  return null;
}
